import { type SetStateAction, type SyntheticEvent, useState } from "react";
import { useNavigate } from "react-router";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { useDeleteUserProfile, useMutateUserProfile } from "src/auth/queries";
import { useTypedLocation } from "../../../utils/hooks/useTypedLocation";
import { navigateToNewState } from "../../../utils/location/navigateToNewState";
import { type UserProfile, getUserEmail } from "../../utils/user";

export function useManageProfile(user: UserProfile) {
  const userProfileMutation = useMutateUserProfile();
  const navigate = useNavigate();
  const location = useTypedLocation();
  const [name, setName] = useState(user.name ?? "");
  const [email, setEmail] = useState(getUserEmail(user) ?? "");
  const [toggle, setToggle] = useState(user.optedInMarketingEmails);
  const disabled =
    (name === user.name && toggle === user.optedInMarketingEmails) || !name;
  const deleteProfile = useDeleteUserProfile();

  function handleNameChange(value: string) {
    setName(value);
  }

  function handleEmailChange(value: string) {
    setEmail(value);
  }

  function handleOnChangeToggle(value: SetStateAction<boolean>) {
    setToggle(value);
  }

  function handleUpdate(event: SyntheticEvent) {
    event.preventDefault();
    sendAnalyticsInteractionEvent({
      category: "UserProfile",
      action: "Click:Update",
      label: `$marketing=${toggle}`,
    });
    userProfileMutation.mutate({
      name,
      optedInMarketingEmails: toggle,
    });
    navigateToNewState(navigate, { preferencesScreen: "main" }, location);
  }

  function handleDeleteProfile() {
    sendAnalyticsInteractionEvent({
      category: "UserProfile",
      action: "Click:Delete",
    });
    deleteProfile.mutate(undefined, {
      onSuccess() {
        navigateToNewState(navigate, { preferencesScreen: "main" }, location);
      },
    });
  }

  return {
    name,
    email,
    toggle,
    handleNameChange,
    handleEmailChange,
    handleOnChangeToggle,
    handleUpdate,
    disabled,
    handleDeleteProfile,
  };
}
